import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Loader2, ChevronDown, ChevronUp, AlertCircle } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { apiClient } from "@/lib/api";

/**
 * Settings-only review UI for plants whose `identification.life_cycle` is
 * `"unknown"` or missing. Renders nothing when the count is zero so the
 * Settings page stays quiet for users whose collection is fully classified.
 *
 * UX contract:
 *  - Card is collapsed by default; header shows the count.
 *  - Expanding fetches the paged list on demand.
 *  - Each row has a Select (annual / biennial / perennial / unknown).
 *    Changing the Select immediately PATCHes the server; on success the
 *    row is optimistically removed from the list (unless the user picks
 *    "unknown" — that stays visible because the plant is still unclassified,
 *    it just gets its `life_cycle_backfilled_at` flag flipped so the
 *    Gemini backfill won't retouch it).
 *  - Header count re-syncs from the /status endpoint after every change so
 *    the outer number matches the visible rows.
 */
export default function UnclassifiedLifeCycleSection({ status, onStatusChange }) {
  const { t } = useTranslation();
  const unclassifiedCount = status?.unclassified ?? 0;
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [savingId, setSavingId] = useState(null);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/plants/unclassified-life-cycle?limit=200");
      setItems(data?.items || []);
    } catch {
      setItems([]);
      toast.error(t("settings.unclassifiedLoadFail"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (expanded && items.length === 0) loadList();
  }, [expanded, items.length, loadList]);

  // Re-fetch when the outer status (unclassified count) changes downward due
  // to some other action. Prevents "ghost rows" for plants that were fixed
  // elsewhere in the app. `expanded` / `loading` are read as guards only —
  // we deliberately don't re-run when they toggle (they'd cause a double
  // fetch), and `loadList` is a stable useCallback([t]).
  useEffect(() => {
    if (expanded && !loading) loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unclassifiedCount]);

  const patchLifeCycle = async (scan_id, life_cycle) => {
    setSavingId(scan_id);
    try {
      await apiClient.patch(`/plants/${scan_id}/life-cycle`, { life_cycle });
      // Row leaves the list unless the user picked "unknown" (still
      // unclassified — but the server marked it reviewed).
      if (life_cycle === "unknown") {
        setItems((prev) => prev.map((it) => it.scan_id === scan_id
          ? { ...it, identification: { ...(it.identification || {}), life_cycle: "unknown" } }
          : it));
      } else {
        setItems((prev) => prev.filter((it) => it.scan_id !== scan_id));
      }
      toast.success(t("settings.unclassifiedSaved", { lc: t(`result.lifeCycle.${life_cycle}`) }));
      onStatusChange?.();
    } catch {
      toast.error(t("settings.unclassifiedSaveFail"));
    } finally {
      setSavingId(null);
    }
  };

  if (unclassifiedCount === 0) return null;

  return (
    <div
      data-testid="unclassified-life-cycle-card"
      className="bg-white rounded-3xl border border-[#E8E5DF] overflow-hidden"
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        data-testid="unclassified-toggle"
        aria-expanded={expanded}
        className="w-full flex items-center gap-3 px-5 py-4 hover:bg-parchment transition text-leaf-ink text-left"
      >
        <span className="text-clay">
          <AlertCircle className="w-4 h-4" strokeWidth={1.5} />
        </span>
        <span className="flex-1 flex flex-col">
          <span>{t("settings.unclassifiedTitle")}</span>
          <span className="text-[11px] text-leaf-mist mt-0.5">
            {t("settings.unclassifiedHint", { n: unclassifiedCount })}
          </span>
        </span>
        <span className="text-leaf-mist" aria-hidden="true">
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </span>
      </button>

      {expanded && (
        <div className="border-t border-[#EFEBE3] px-5 py-4">
          {loading ? (
            <div
              data-testid="unclassified-loading"
              className="flex items-center gap-2 text-leaf-mist text-sm py-6 justify-center"
              role="status"
              aria-live="polite"
            >
              <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
              {t("common.loading")}
            </div>
          ) : items.length === 0 ? (
            <p className="text-leaf-mist text-sm py-6 text-center">
              {t("settings.unclassifiedEmpty")}
            </p>
          ) : (
            <ul className="divide-y divide-[#EFEBE3]" data-testid="unclassified-list">
              {items.map((it) => {
                const id = it.identification || {};
                const current = (id.life_cycle || "").toLowerCase();
                const isSaving = savingId === it.scan_id;
                return (
                  <li
                    key={it.scan_id}
                    data-testid={`unclassified-row-${it.scan_id}`}
                    className="flex items-center gap-3 py-3"
                  >
                    {it.thumb_base64 ? (
                      <img
                        src={it.thumb_base64}
                        alt=""
                        className="w-11 h-11 rounded-lg object-cover flex-shrink-0"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-11 h-11 rounded-lg bg-parchment flex-shrink-0" aria-hidden="true" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-leaf-ink text-sm truncate">
                        {id.common_name || t("gallery.card.unknown")}
                      </div>
                      {id.scientific_name && (
                        <div className="text-[11px] italic text-leaf-mist truncate">
                          {id.scientific_name}
                        </div>
                      )}
                    </div>
                    <Select
                      value={current || undefined}
                      onValueChange={(v) => patchLifeCycle(it.scan_id, v)}
                      disabled={isSaving}
                    >
                      <SelectTrigger
                        className="w-[132px] h-9 bg-parchment/40 border-[#E8E5DF]"
                        data-testid={`unclassified-select-${it.scan_id}`}
                        aria-label={t("settings.unclassifiedRowAria", { name: id.common_name || "" })}
                      >
                        {isSaving ? (
                          <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
                        ) : (
                          <SelectValue placeholder={t("settings.unclassifiedPickerPlaceholder")} />
                        )}
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="annual">{t("result.lifeCycle.annual")}</SelectItem>
                        <SelectItem value="biennial">{t("result.lifeCycle.biennial")}</SelectItem>
                        <SelectItem value="perennial">{t("result.lifeCycle.perennial")}</SelectItem>
                        <SelectItem value="unknown">{t("result.lifeCycle.unknown")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
